'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface ComparisonFeature {
  feature: string;
  basic: boolean;
  standard: boolean;
  premium: boolean;
}

interface ServiceComparisonMatrixProps {
  features: ComparisonFeature[];
}

export default function ServiceComparisonMatrix({
  features,
}: ServiceComparisonMatrixProps) {
  const [isExpanded, setIsExpanded] = useState(false);

  const displayedFeatures = isExpanded ? features : features.slice(0, 6);

  return (
    <div className="bg-card rounded-lg shadow-subtle p-6 mb-8">
      <h2 className="text-xl font-headline font-bold text-primary mb-6">
        Service Level Comparison
      </h2>

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="border-b border-border">
              <th className="text-left py-3 px-4 text-sm font-headline font-semibold text-textPrimary">
                Features
              </th>
              <th className="text-center py-3 px-4 text-sm font-headline font-semibold text-textPrimary">
                Basic
              </th>
              <th className="text-center py-3 px-4 text-sm font-headline font-semibold text-textPrimary">
                Standard
              </th>
              <th className="text-center py-3 px-4 text-sm font-headline font-semibold text-textPrimary">
                Premium
              </th>
            </tr>
          </thead>
          <tbody>
            {displayedFeatures.map((item, index) => (
              <tr
                key={index}
                className="border-b border-border hover:bg-muted transition-colors duration-200"
              >
                <td className="py-3 px-4 text-sm font-body text-textPrimary">
                  {item.feature}
                </td>
                <td className="py-3 px-4 text-center">
                  {item.basic ? (
                    <Icon
                      name="CheckCircleIcon"
                      size={20}
                      className="text-success mx-auto"
                    />
                  ) : (
                    <Icon
                      name="XCircleIcon"
                      size={20}
                      className="text-textSecondary mx-auto"
                    />
                  )}
                </td>
                <td className="py-3 px-4 text-center">
                  {item.standard ? (
                    <Icon
                      name="CheckCircleIcon"
                      size={20}
                      className="text-success mx-auto"
                    />
                  ) : (
                    <Icon
                      name="XCircleIcon"
                      size={20}
                      className="text-textSecondary mx-auto"
                    />
                  )}
                </td>
                <td className="py-3 px-4 text-center">
                  {item.premium ? (
                    <Icon
                      name="CheckCircleIcon"
                      size={20}
                      className="text-success mx-auto"
                    />
                  ) : (
                    <Icon
                      name="XCircleIcon"
                      size={20}
                      className="text-textSecondary mx-auto"
                    />
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {features.length > 6 && (
        <div className="mt-4 text-center">
          <button
            onClick={() => setIsExpanded(!isExpanded)}
            className="inline-flex items-center px-4 py-2 text-sm font-cta font-semibold text-secondary hover:text-primary transition-colors duration-300"
          >
            {isExpanded ? 'Show Less' : 'Show All Features'}
            <Icon
              name={isExpanded ? 'ChevronUpIcon' : 'ChevronDownIcon'}
              size={16}
              className="ml-2"
            />
          </button>
        </div>
      )}
    </div>
  );
}